/**
 * Column definitions and row actions for the Scheduled Scans DataTable.
 *
 * Extracted from SchedulePage for the same reason `projectsPageColumns.tsx`
 * was extracted from ProjectsPage: the page stays a wiring file, and the cell
 * rendering is testable in isolation.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { LinkSquare01Icon } from '@hugeicons/core-free-icons';
import type { ColumnDef, RowAction, SubMenuItem } from '../shared/DataTableTypes';
import type { CadenceValue, ScheduledScanRow } from '../../lib/api/scheduledScans';
import { CADENCE_LABEL, SCAN_TYPE_LABEL, isRunOverdue, lastScanLabel, nextRunLabel, statusLabel } from './scheduleRowCopy';

const CADENCE_ORDER: CadenceValue[] = ['daily', 'weekly', 'monthly'];

/** Green while it runs, amber when the site stopped answering, grey when parked. */
const STATUS_DOT: Record<ScheduledScanRow['status'], string> = {
  active: 'bg-green-500',
  paused: 'bg-gray-400',
  paused_unreachable: 'bg-amber-500',
  paused_bot_protection: 'bg-amber-500',
  paused_page_target: 'bg-amber-500',
  paused_snapshot_limit: 'bg-amber-500',
};

const PAUSE_DISABLED_TITLE = 'This schedule is already paused';
const RESUME_DISABLED_TITLE = 'This schedule is already active';
// Running a paused schedule would scan a site the user asked us to stop
// watching, so the action points at the item that fixes it instead.
const RUN_DISABLED_TITLE = 'Resume this schedule before running it';
/**
 * The second reason Run now can be disabled, and why it needs its own sentence.
 *
 * A manual run is a full Puppeteer browser plus an AI pass, measured at ~16s.
 * Before this, nothing on the page changed for that entire window (0 of 63
 * in-flight frames, 2026-08-17), so a user reopened the menu and pressed again
 * and got a SECOND real scan: `POST /run` answered [200, 200]. Eight concurrent
 * presses produced five browsers and three 429s.
 */
const RUN_IN_FLIGHT_TITLE = 'A scan is already running for this page';
/** What the Last-scan cell says instead of its stale value while a run is live. */
export const SCANNING_LABEL = 'Scanning...';

export function isSchedulePaused(schedule: Pick<ScheduledScanRow, 'status'>): boolean {
  return schedule.status !== 'active';
}

/**
 * `now` is passed in rather than read inside the renderer so the "in 3 days"
 * phrasing is deterministic per render pass (every row reads the same clock)
 * and testable without faking timers.
 *
 * `runningIds` is the set of schedules whose manual run is in flight right now.
 * Empty by default, so a caller that never runs one is unchanged.
 */
export interface ScheduleColumnReviewOpts {
  /**
   * Open a team review on the scan's own canvas. The Findings column's "View"
   * calls this, the SAME handler the row menu's "Review findings" calls
   * (Emily, 2026-09-09: "View should take the user to Review Findings, not
   * something different"); the column never links the report.
   */
  onReviewFindings: (schedule: ScheduledScanRow) => void;
  /** Schedules whose findings-review create is in flight. */
  reviewingIds: ReadonlySet<string>;
}

export function buildScheduleColumns(
  now: Date,
  runningIds: ReadonlySet<string> = new Set(),
  review: ScheduleColumnReviewOpts,
): ColumnDef<ScheduledScanRow>[] {
  return [
    {
      header: 'URL',
      render: (schedule) => {
        // The row's primary identifier IS its link to the row's detail view,
        // the pattern the Projects table already uses (projectsPageColumns.tsx
        // links the project name, not a trailing "Open" affordance). Structure
        // is copied from there so the two tables cannot drift: `group` on the
        // anchor, an inner flex row, and `truncate` on the label only.
        //
        // A schedule that has never run has no report, so it stays plain text
        // rather than becoming a link to nowhere.
        if (!schedule.lastShareUrl) {
          return (
            <span className="block max-w-xs truncate text-sm font-medium text-gray-900" title={schedule.url}>
              {schedule.url}
            </span>
          );
        }

        // The accessible name states the destination and CONTAINS the visible
        // URL, so WCAG 2.5.3 (Label in Name) holds while a screen-reader user
        // is told this opens a report rather than the page being watched.
        const label = `View the latest report for ${schedule.url}`;

        return (
          <a
            href={schedule.lastShareUrl}
            onClick={schedule.scanType === 'auto_review' ? (event) => {
              event.preventDefault();
              review.onReviewFindings(schedule);
            } : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            // Doubles as the reveal for a URL the cell had to truncate.
            title={label}
            className="group block max-w-xs"
          >
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 truncate transition-colors">
                {schedule.url}
              </span>
              {/* The only at-rest difference between a row with a report and a
                  row without one. Hover colour alone would leave the two
                  indistinguishable until pointed at (WCAG 1.4.1), and says
                  nothing about the new tab. Same glyph and size the dashboard
                  already uses for external links (ExportedToSection.tsx:87).

                  gray-500, NOT the gray-400 this first shipped as. Because the
                  glyph carries meaning it has to clear WCAG 1.4.11's 3:1
                  non-text floor, and gray-400 (#8b93a5) measures 3.08:1 on
                  white but 2.97:1 on DataTable's odd-row `bg-gray-50/60`
                  (DataTable.tsx:142), so it failed on every other row.
                  gray-500 (#6b7280) is 4.66:1 there and still reads as
                  subordinate to the gray-900 URL beside it. Pinned live by
                  e2e/verify-schedule-url-report-link.ts, which computes the
                  ratio from painted pixels rather than from these numbers. */}
              <HugeiconsIcon
                icon={LinkSquare01Icon}
                size={12}
                strokeWidth={2}
                color="currentColor"
                className="w-3 h-3 shrink-0 text-gray-500 group-hover:text-primary-600 transition-colors"
                aria-hidden="true"
              />
            </span>
          </a>
        );
      },
    },
    {
      header: 'Project',
      className: 'hidden sm:table-cell',
      render: (schedule) => (
        // A blank cell reads as a rendering bug. A schedule whose project row
        // is gone says so.
        <span className="text-sm text-gray-600">{schedule.projectName ?? 'Unknown project'}</span>
      ),
    },
    {
      header: 'Type',
      className: 'hidden md:table-cell',
      render: (schedule) => <span className="text-sm text-gray-600">{SCAN_TYPE_LABEL[schedule.scanType]}</span>,
    },
    {
      header: 'Cadence',
      className: 'hidden md:table-cell',
      render: (schedule) => <span className="text-sm text-gray-600">{CADENCE_LABEL[schedule.cadence]}</span>,
    },
    {
      header: 'Last scan',
      // Reports what the last run found, and nothing else. The "View report"
      // link that used to sit under this text moved onto the URL cell: it gave
      // every row a second link whose text was identical to every other row's,
      // which is the ambiguous-link-text shape WCAG 2.4.4 warns about.
      //
      // While a manual run is in flight this cell is where the run is reported,
      // because it is the cell the run is about to rewrite: showing the stale
      // value for 16 seconds is what made Run now look like it did nothing.
      // aria-live so a screen-reader user is told without having to re-read the
      // row, and aria-busy on the row's own value while it is provisional.
      render: (schedule) =>
        runningIds.has(schedule.id) ? (
          <span className="text-sm text-gray-600" role="status" aria-live="polite">
            {SCANNING_LABEL}
          </span>
        ) : (
          <span className="text-sm text-gray-600">{lastScanLabel(schedule)}</span>
        ),
    },
    {
      header: 'Next scan',
      className: 'hidden lg:table-cell',
      render: (schedule) => {
        // A stuck schedule has to LOOK stuck. Saying "overdue by 41 days" in
        // the same muted grey as a healthy "in 7 days" is why a real 41-day
        // outage sat unnoticed on a page someone had open.
        //
        // yellow-800 (#854d0e), NOT the obvious --color-warning-dark. That
        // token is amber-700, and this table's own ground rules it out:
        // DataTable.tsx:142 stripes odd rows, and index.css:210 records the
        // measurement that settled the severity ramp on exactly this problem,
        // "orange-700 4.45, gray-500 4.34, amber-700 4.43" against a 4.5
        // floor. Every near-step passes on white and fails on the stripe.
        // yellow-800 is the step that clears both, and it keeps this on the
        // same recognizable ramp rather than adding a one-off hex.
        //
        // The colour REINFORCES the sentence and never carries it: the cell
        // still reads the lateness in words, so WCAG 1.4.1 holds.
        const overdue = isRunOverdue(schedule.nextRunAt, now);
        return (
          <span className={`text-sm ${overdue ? 'text-yellow-800 font-medium' : 'text-gray-500'}`}>
            {nextRunLabel(schedule.nextRunAt, now)}
          </span>
        );
      },
    },
    {
      header: 'Status',
      render: (schedule) => (
        <span className="inline-flex items-center gap-2 text-sm text-gray-600">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[schedule.status]}`} aria-hidden="true" />
          {statusLabel(schedule.status)}
        </span>
      ),
    },
    {
      header: 'Findings',
      // LAST data column on purpose, so it renders directly left of the row's
      // actions menu (Emily, 2026-09-08: "add a column before actions that has
      // the View Findings link to each report"). Never hidden at a breakpoint:
      // it is the row's one explicit affordance, and DataTable scrolls rather
      // than drops columns.
      //
      // It OPENS THE TEAM REVIEW, not the report (Emily, 2026-09-09: "View
      // should take the user to Review Findings, not something different").
      // Until then this cell linked `lastShareUrl`, the share report, whose
      // page screenshot is the first 1280x800 of the page (freeCheckerQueue.ts
      // crops it) and so cannot pin a finding below the fold; the review
      // renders the scan's stored DOM full height with every finding pinned.
      // So this is a BUTTON that mints that review through the same handler
      // as the menu item, gated by the same predicate, and the header stopped
      // saying "Report" the day the cell stopped opening one. The URL cell
      // keeps its report link; retiring it is a separate product call.
      render: (schedule) => {
        if (!schedule.lastScannedAt) {
          // A blank cell reads as a rendering bug, and a disabled control
          // nobody hovers reads as broken, so the never-run state says so in
          // words. gray-500, not gray-400: on DataTable's zebra stripe
          // gray-400 measures 2.97:1 (see the URL cell's glyph note), and
          // this is text, which needs 4.5:1.
          return <span className="text-sm text-gray-500 whitespace-nowrap">No findings yet</span>;
        }
        // Same reason, same words as the menu item: a row scanned before the
        // snapshot existed (prod 2026-09-09: 5 of 82 active schedules) keeps
        // the control, disabled, rather than being sent somewhere else.
        const disabledReason = reviewFindingsDisabledReason(schedule, review.reviewingIds);
        // The visible text is one word, "View" (Emily, 2026-09-08: "lets keep
        // it short to just View"), and WCAG 2.5.3 wants the visible text
        // CONTAINED in the accessible name, so the name starts with it rather
        // than with the menu item's verb ("Review findings for ..." was tried
        // first and the harness's Label-in-Name check refused it, mini
        // 2026-09-09). The name carries the row's URL so a screen reader's
        // controls list never shows N identical entries (WCAG 2.4.4).
        const label = `View findings for ${schedule.url}`;
        return (
          <button
            type="button"
            aria-label={label}
            title={disabledReason ?? undefined}
            disabled={disabledReason !== null}
            onClick={() => review.onReviewFindings(schedule)}
            // The dashboard's text-link idiom (BrandingSection.tsx:342,
            // ReviewHandoffPage.tsx:149) on a button, so the cell reads the
            // same as before while doing what the menu item does. nowrap so
            // the word never splits when the table is squeezed; it scrolls.
            className="text-sm font-medium text-primary-600 hover:underline whitespace-nowrap disabled:text-gray-500 disabled:no-underline disabled:cursor-not-allowed"
          >
            <span className="text-cap-trim block">View</span>
          </button>
        );
      },
    },
  ];
}

/** Why "Capture Issues" refuses an unreachable site. */
const REVIEW_UNREACHABLE_TITLE = 'This site stopped answering, so a review cannot load it';
const REVIEW_BOT_WALL_TITLE = "This site's bot protection blocks our scanner, so a review cannot load it";
/**
 * Why "Review findings" refuses a row with no stored canvas.
 *
 * Two causes land here and the copy covers both without guessing which: a row
 * that has never scanned, and one scanned before the snapshot existed (or
 * whose capture failed). Either way the fix is the same, so the sentence names
 * it rather than explaining our storage.
 */
const REVIEW_FINDINGS_NO_CANVAS_TITLE = 'Run a scan first, then you can review its findings on the page';
/** Why it refuses a second click while the first review is still being created. */
const REVIEW_IN_FLIGHT_TITLE = 'Opening a team review for this page';

/**
 * Why Review findings is refused on this row right now, or null when it can
 * open. ONE predicate for the two doors (the Findings column's "View" and the
 * row menu's "Review findings"), so they can never disagree about which rows
 * can be reviewed or what to say when one cannot.
 */
export function reviewFindingsDisabledReason(
  schedule: Pick<ScheduledScanRow, 'id' | 'hasStoredCanvas'>,
  reviewingIds: ReadonlySet<string>,
): string | null {
  if (reviewingIds.has(schedule.id)) return REVIEW_IN_FLIGHT_TITLE;
  if (schedule.hasStoredCanvas !== true) return REVIEW_FINDINGS_NO_CANVAS_TITLE;
  return null;
}

export function buildScheduleRowActions(opts: {
  /** Resolved project role; false while roles are loading or unavailable. */
  canManage: (schedule: ScheduledScanRow) => boolean;
  onCaptureIssues: (schedule: ScheduledScanRow) => void;
  /** Open a team review on the scan's own canvas. */
  onReviewFindings: (schedule: ScheduledScanRow) => void;
  onRunNow: (schedule: ScheduledScanRow) => void;
  /** Hand the page to the extension (the user's own browser). Only offered on a bot-walled row. */
  onScanWithExtension: (schedule: ScheduledScanRow) => void;
  onPause: (schedule: ScheduledScanRow) => void;
  onResume: (schedule: ScheduledScanRow) => void;
  onCadenceChange: (schedule: ScheduledScanRow, cadence: CadenceValue) => void;
  setConfirmDelete: (v: { id: string; url: string } | null) => void;
  /** Schedules whose manual run is in flight. Empty by default. */
  runningIds?: ReadonlySet<string>;
  /** Schedules whose team-review create is in flight. Empty by default. */
  capturingIds?: ReadonlySet<string>;
  /** Schedules whose findings-review create is in flight. Empty by default. */
  reviewingIds?: ReadonlySet<string>;
}): RowAction<ScheduledScanRow>[] {
  const { onCaptureIssues, onReviewFindings, onRunNow, onScanWithExtension, onPause, onResume, onCadenceChange, setConfirmDelete } = opts;
  const runningIds = opts.runningIds ?? new Set<string>();
  const capturingIds = opts.capturingIds ?? new Set<string>();
  const reviewingIds = opts.reviewingIds ?? new Set<string>();
  const readOnly = (schedule: ScheduledScanRow) => !opts.canManage(schedule);

  return [
    // First, because it is the value action: the row's URL cell opens the READ
    // artifact (the report, no session, no create controls) and this opens the
    // surface that can ACT.
    //
    // NAMED FOR WHAT IT DOES (2026-08-22). It was "Review findings" until a
    // real-browser measurement showed the label promising something the
    // destination does not carry: on a row holding 8 stored findings the review
    // opened showing 0, offering the audit panel's idle "Scan for accessibility
    // issues" instead, reproduced on 4 runs
    // (docs/test-reports/2026-08-22-schedule-review-findings-gap/). The stored
    // findings are never handed to a review -- App.tsx's mount effect calls
    // prepareForOpen, which resets the panel to 'idle'. The old label also made
    // one intent wear three names: this item, the "Capture Issues" dialog it
    // opened, and that dialog's "Review" button.
    //
    // "Capture Issues" is the vocabulary every other surface already uses
    // (IssuesPage, ProjectsPage, the modal's own title; standardised
    // 2026-08-17), and it is true on a never-scanned row too -- capturing
    // issues on a page needs no prior scan, which is why this action stays
    // enabled in that state rather than being gated on findings existing.
    //
    // Routing the STORED findings into issues is a separate, larger change:
    // the rescan handoff is bound to the baseline's OWNER while this list is
    // team-scoped, so it needs dual-auth on two issue-creating routes plus a
    // product call about teammates creating issues from another member's scan.
    // FIRST, above Capture Issues, because on a row that has findings this is
    // the question the user came with: show me what you found, where it is.
    //
    // Distinct from Capture Issues, which stays. That one captures the page
    // FRESH and is right for filing issues against the page as it is NOW. This
    // one opens the scan's OWN render so the stored findings pin where they
    // actually happened. Two different questions, two actions.
    //
    // A PAUSED schedule keeps it: pausing stops future scans and says nothing
    // about findings already collected. The server allows it for the same
    // reason, so the button and the endpoint cannot disagree.
    {
      label: 'Review findings',
      onClick: onReviewFindings,
      // The Findings column's "View" reads the same predicate; see it.
      disabled: (schedule) => reviewFindingsDisabledReason(schedule, reviewingIds) !== null,
      disabledTitle: (schedule) => reviewFindingsDisabledReason(schedule, reviewingIds) ?? '',
    },
    {
      label: 'Capture issues',
      hidden: readOnly,
      onClick: onCaptureIssues,
      // Two reasons, and the in-flight one is PER ROW: a single boolean would
      // freeze every row while one review was being created, which is wrong on
      // a table whose whole job is several independently watched pages (same
      // shape as Run now).
      //
      // Only unreachability disables it otherwise. A deliberately PAUSED
      // schedule keeps the action: pausing the scan says nothing about the
      // page, which still exists and can be reviewed. paused_unreachable means
      // the site stopped answering our probes, so the review proxy would render
      // a broken canvas -- the samaretstp same-day-churn shape the preflight
      // exists to prevent.
      disabled: (schedule) =>
        schedule.status === 'paused_unreachable' ||
        schedule.status === 'paused_bot_protection' ||
        schedule.status === 'paused_page_target' ||
        schedule.status === 'paused_snapshot_limit' ||
        capturingIds.has(schedule.id),
      disabledTitle: (schedule) =>
        capturingIds.has(schedule.id)
          ? REVIEW_IN_FLIGHT_TITLE
          : schedule.status === 'paused_bot_protection'
            ? REVIEW_BOT_WALL_TITLE
            : schedule.status === 'paused_page_target'
              ? 'Use a public page, or open the intended page with the extension'
              : schedule.status === 'paused_snapshot_limit'
                ? 'The saved page exceeds the review size limit; use the extension'
                : REVIEW_UNREACHABLE_TITLE,
    },
    // Second: the repair action, for a schedule that is not doing what it
    // says. Before it existed, a row the cron skipped rendered "Active /
    // within the hour" forever with nothing to press (prod 2026-08-16: 41
    // days). It sat first until Review findings arrived; the value action now
    // outranks the repair action because a healthy table needs it more often.
    // Only on a row the site's bot protection paused (2026-09-22): the one
    // scan that works there runs in the user's own browser, which the site
    // already lets in. Hidden elsewhere rather than disabled: on every other
    // row it would be a control with nothing to say.
    {
      label: 'Scan with the extension',
      onClick: onScanWithExtension,
      hidden: (schedule) => readOnly(schedule) || !['paused_bot_protection', 'paused_page_target', 'paused_snapshot_limit'].includes(schedule.status),
    },
    {
      label: 'Run now',
      hidden: readOnly,
      onClick: onRunNow,
      // Two reasons, and the guard is PER ROW rather than per mutation: a
      // single in-flight boolean would freeze every row while one scanned,
      // which is wrong on a table whose whole job is several watched pages.
      disabled: (schedule) => isSchedulePaused(schedule) || runningIds.has(schedule.id),
      disabledTitle: (schedule) =>
        runningIds.has(schedule.id) ? RUN_IN_FLIGHT_TITLE : RUN_DISABLED_TITLE,
    },
    // Pause and Resume are both listed and one is disabled per row: RowAction
    // labels are static across rows, so a per-row label swap is not available
    // (the same mechanic ProjectsPage uses for owner-only Archive/Delete).
    {
      label: 'Pause',
      hidden: readOnly,
      onClick: onPause,
      disabled: isSchedulePaused,
      disabledTitle: PAUSE_DISABLED_TITLE,
    },
    {
      label: 'Resume',
      hidden: readOnly,
      onClick: onResume,
      disabled: (schedule) => !isSchedulePaused(schedule),
      disabledTitle: RESUME_DISABLED_TITLE,
    },
    {
      label: 'Change cadence',
      hidden: readOnly,
      // The parent item only opens the flyout; RowActionMenu never calls
      // onClick for an action that has a submenu.
      onClick: () => {},
      getSubmenu: (schedule): SubMenuItem[] =>
        CADENCE_ORDER.map((cadence) => ({
          label: CADENCE_LABEL[cadence],
          checked: schedule.cadence === cadence,
          onClick: () => onCadenceChange(schedule, cadence),
        })),
    },
    {
      label: 'Delete',
      hidden: readOnly,
      onClick: (schedule) => setConfirmDelete({ id: schedule.id, url: schedule.url }),
      destructive: true,
    },
  ];
}
